// src/components/AddCompanyForm/index.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  Paper,
  Box,
  Typography,
  Tooltip,
  Alert,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ActionBar from "./ActionBar";
import CompanyEssentialsCard from "./CompanyEssentialsCard";
import IdentityVisualCard from "./IdentityVisualCard";
import ContactCard from "./ContactCard";
import AddressLegalCard from "./AddressLegalCard";
import SocialsCard from "./SocialsCard";
import RegionsCard, { useRegionKeys } from "./RegionsCard";
import CepRangesCard from "./CepRangesCard";
import PermissionsCard from "./PermissionsCard";
import { useTheme } from "@mui/material/styles";
import { toHex, onlyDigits, slugify, isHttps, uid, validCep8 } from "./utils";
import { useAuthContext } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { fetchData } from "../../api/client";

/** Container do formulário de empresas (estado + submit) */
export default function AddCompanyForm({ company, onClose, onSubmit, onSaved, isLoading }) {
  const theme = useTheme();
  const { userData } = useAuthContext();
  const { t } = useTranslation("settings");

  const [errorMessage, setErrorMessage] = useState(null);
  const [busyUpload, setBusyUpload] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Estado base
  const [form, setForm] = useState(() => ({
    id: company?.id ?? undefined,
    nome: company?.nome ?? "",
    codigo_interno: company?.codigo_interno ?? "",
    status: Number.isFinite(company?.status) ? Number(company.status) : 1,
    primary_color: toHex(company?.primary_color || ""),
    email: company?.email ?? "",
    telefone: company?.telefone ?? "",
    telefone_adicional: company?.telefone_adicional ?? "",
    support_email: company?.support_email ?? "",
    support_whatsapp: company?.support_whatsapp ?? "",
    site: company?.site ?? "",
    endereco: company?.endereco ?? "",
    logo: company?.logo ?? null,
    _logoPreview: company?.logo ?? null,

    cnpj: company?.cnpj ?? "",
    atendimento_horario: company?.atendimento_horario ?? "",
    facebook: company?.facebook ?? "",
    instagram: company?.instagram ?? "",
    youtube: company?.youtube ?? "",
    linkedin: company?.linkedin ?? "",
  }));

  // Regiões
  const [regioes, setRegioes] = useState(() =>
    (Array.isArray(company?.regioes) ? company.regioes : []).map((r, idx) => ({
      _id: uid(),
      region_key: r.region_key,
      label: r.label,
      ordem: Number.isFinite(+r.ordem) ? +r.ordem : idx,
      status: Number(r.status) === 0 ? 0 : 1,
      restrita: Number(r.restrita) === 1 ? 1 : 0,
      has_region_access_password: Boolean(r.has_region_access_password),
      nova_senha_acesso: "",
    }))
  );
  const regionKeys = useRegionKeys(regioes);

  // Faixas de CEP
  const [cepRanges, setCepRanges] = useState(() =>
    (Array.isArray(company?.cepRanges) ? company.cepRanges : []).map((r, idx) => ({
      _id: uid(),
      region_key: r.region_key,
      start_cep: r.start_cep || r.start || "",
      end_cep: r.end_cep || r.end || "",
      target_cod_interno: r.target_cod_interno || r.company || "",
      ordem: Number.isFinite(+r.ordem) ? +r.ordem : idx,
      status: Number(r.status) === 0 ? 0 : 1,
    }))
  );

  /** Usuários com acesso ao projeto — na criação, aplicado via PUT /companies/:id/acl após o POST. */
  const [draftAclUsers, setDraftAclUsers] = useState([]);

  /** Slug gerado do nome na inclusão; ao editar o slug manualmente, deixa de sobrescrever pelo nome. */
  const slugTouchedRef = useRef(false);
  const prevCompanyIdRef = useRef(company?.id);

  useEffect(() => {
    const prev = prevCompanyIdRef.current;
    prevCompanyIdRef.current = company?.id;
    if (prev != null && company?.id == null) slugTouchedRef.current = false;
  }, [company?.id]);

  useEffect(() => {
    if (form.id || slugTouchedRef.current) return;
    const next = slugify(form.nome);
    setForm((p) => (p.codigo_interno === next ? p : { ...p, codigo_interno: next }));
  }, [form.nome, form.id]);

  useEffect(() => {
    if (company?.id) return;
    setDraftAclUsers((prev) => {
      if (prev.length) return prev;
      if (!userData?.id) return [];
      return [
        {
          id: userData.id,
          nome: userData.nome || "",
          email: userData.email || "",
        },
      ];
    });
  }, [company?.id, userData?.id, userData?.nome, userData?.email]);

  // Atualiza quando abrir em modo edição (troca de empresa)
  useEffect(() => {
    setForm({
      id: company?.id ?? undefined,
      nome: company?.nome ?? "",
      codigo_interno: company?.codigo_interno ?? "",
      status: Number.isFinite(company?.status) ? Number(company.status) : 1,
      primary_color: toHex(company?.primary_color || ""),
      email: company?.email ?? "",
      telefone: company?.telefone ?? "",
      telefone_adicional: company?.telefone_adicional ?? "",
      support_email: company?.support_email ?? "",
      support_whatsapp: company?.support_whatsapp ?? "",
      site: company?.site ?? "",
      endereco: company?.endereco ?? "",
      logo: company?.logo ?? null,
      _logoPreview: company?.logo ?? null,

      cnpj: company?.cnpj ?? "",
      atendimento_horario: company?.atendimento_horario ?? "",
      facebook: company?.facebook ?? "",
      instagram: company?.instagram ?? "",
      youtube: company?.youtube ?? "",
      linkedin: company?.linkedin ?? "",
    });

    const nextRegioes = Array.isArray(company?.regioes)
      ? company.regioes.map((r, idx) => ({
          _id: uid(),
          region_key: r.region_key,
          label: r.label,
          ordem: Number.isFinite(+r.ordem) ? +r.ordem : idx,
          status: Number(r.status) === 0 ? 0 : 1,
          restrita: Number(r.restrita) === 1 ? 1 : 0,
          has_region_access_password: Boolean(r.has_region_access_password),
          nova_senha_acesso: "",
        }))
      : [];
    setRegioes(nextRegioes);

    const nextCepRanges = Array.isArray(company?.cepRanges)
      ? company.cepRanges.map((r, idx) => ({
          _id: uid(),
          region_key: r.region_key,
          start_cep: r.start_cep || r.start || "",
          end_cep: r.end_cep || r.end || "",
          target_cod_interno: r.target_cod_interno || r.company || "",
          ordem: Number.isFinite(+r.ordem) ? +r.ordem : idx,
          status: Number(r.status) === 0 ? 0 : 1,
        }))
      : [];
    setCepRanges(nextCepRanges);
  }, [company?.id]); // eslint-disable-line

  const isEdit = Boolean(form.id);

  // Helpers
  const setFlash = (msg) => {
    setErrorMessage(msg);
    setTimeout(() => setErrorMessage(null), 3000);
  };

  // Submit
  const save = async (e) => {
    e.preventDefault?.();
    setErrorMessage(null);

    if (!form.nome.trim()) return setFlash(t("addCompanyForm.flashNameRequired"));

    if (!form.id && draftAclUsers.length === 0) {
      return setFlash(t("addCompanyForm.flashAclUsersRequired"));
    }

    let projeto;
    let regioesOut;
    let rangesOut;

    if (!form.id) {
      const slugCreate = form.codigo_interno.trim();
      if (slugCreate && slugCreate !== slugify(slugCreate))
        return setFlash(t("addCompanyForm.flashSlugInvalid"));

      projeto = {
        nome: form.nome.trim(),
        status: Number(form.status) === 1 ? 1 : 0,
        codigo_interno: slugCreate || null,
        primary_color: null,
        logo: null,
        email: null,
        telefone: null,
        telefone_adicional: null,
        support_email: null,
        support_whatsapp: null,
        site: null,
        endereco: null,
        cnpj: null,
        atendimento_horario: null,
        facebook: null,
        instagram: null,
        youtube: null,
        linkedin: null,
        id_usuario: company?.id_usuario ?? userData?.id ?? null,
        id_usuario_ultima_alteracao: userData?.id ?? null,
      };
      regioesOut = [];
      rangesOut = [];
    } else {
      const slug = form.codigo_interno.trim();
      if (slug && slug !== slugify(slug))
        return setFlash(t("addCompanyForm.flashSlugInvalid"));

      const color = toHex(form.primary_color);
      if (form.primary_color && !color)
        return setFlash(t("addCompanyForm.flashColorInvalid"));

      if (form.site && !isHttps(form.site))
        return setFlash(t("addCompanyForm.flashSiteHttps"));

      const socials = [
        ["facebook", form.facebook],
        ["instagram", form.instagram],
        ["youtube", form.youtube],
        ["linkedin", form.linkedin],
      ];
      for (const [key, val] of socials) {
        if (val && !isHttps(val))
          return setFlash(t("addCompanyForm.flashSocialUrl", { field: key.toUpperCase() }));
      }

      projeto = {
        id: form.id,
        nome: form.nome.trim(),
        codigo_interno: slug || null,
        status: Number(form.status) === 1 ? 1 : 0,
        primary_color: color || null,
        logo: form.logo || null,

        email: form.email.trim() || null,
        telefone: form.telefone.trim() || null,
        telefone_adicional: form.telefone_adicional.trim() || null,
        support_email: form.support_email.trim() || null,
        support_whatsapp: onlyDigits(form.support_whatsapp || "") || null,

        site: form.site.trim() || null,
        endereco: form.endereco.trim() || null,

        cnpj: onlyDigits(form.cnpj || "") || null,
        atendimento_horario: form.atendimento_horario.trim() || null,

        facebook: form.facebook.trim() || null,
        instagram: form.instagram.trim() || null,
        youtube: form.youtube.trim() || null,
        linkedin: form.linkedin.trim() || null,

        id_usuario: company?.id_usuario ?? userData?.id ?? null,
        id_usuario_ultima_alteracao: userData?.id ?? null,
      };

      regioesOut = regioes.map((r, idx) => {
        const base = {
          region_key: r.region_key,
          label: r.label,
          ordem: Number.isFinite(+r.ordem) ? +r.ordem : idx,
          status: Number(r.status) === 1 ? 1 : 0,
          restrita: Number(r.restrita) === 1 ? 1 : 0,
          _id: r._id,
        };
        const pw = String(r.nova_senha_acesso || "").trim();
        return pw ? { ...base, nova_senha_acesso: pw } : base;
      });

      rangesOut = cepRanges
        .map((r, idx) => {
          const start = onlyDigits(r.start_cep);
          const end = onlyDigits(r.end_cep);
          const region_key = String(r.region_key || "").trim();
          const target = slugify(r.target_cod_interno);
          if (!region_key || !validCep8(start) || !validCep8(end) || !target) return null;
          if (Number(start) > Number(end)) return null;
          return {
            region_key,
            start_cep: start,
            end_cep: end,
            target_cod_interno: target,
            status: Number(r.status) === 1 ? 1 : 0,
            ordem: Number.isFinite(+r.ordem) ? +r.ordem : idx,
            _id: r._id,
          };
        })
        .filter(Boolean);
    }

    try {
      const res = await onSubmit({ projeto, regioes: regioesOut, cepRanges: rangesOut });
      let aclApplyFailed = false;
      if (!form.id) {
        const createdId = res?.data?.id;
        const users = draftAclUsers.map((u) => Number(u.id)).filter(Boolean);
        if (createdId && users.length) {
          try {
            await fetchData(`/companies/${createdId}/acl`, "PUT", { users });
          } catch {
            aclApplyFailed = true;
          }
        }
      }
      onSaved?.({ aclApplyFailed });
    } catch {
      /* erros de salvamento tratados no pai (mutate / snack) */
    }
  };

  const disabled = !form.nome.trim() || busyUpload;

  return (
    <Paper
      component="form"
      onSubmit={save}
      elevation={3}
      sx={{
        p: 2,
        width: "95%",
        m: "0 auto",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))"
            : "linear-gradient(180deg, #fff, #fafafa)",
      }}
    >
      {/* Título */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Typography variant="h6">{isEdit ? t("addCompanyForm.titleEdit") : t("addCompanyForm.titleAdd")}</Typography>
        <Tooltip
          title={isEdit ? t("addCompanyForm.headerTooltip") : t("addCompanyForm.headerTooltipAdd")}
        >
          <InfoOutlinedIcon sx={{ ml: 1, fontSize: 18, color: "text.secondary" }} />
        </Tooltip>
      </Box>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <CompanyEssentialsCard
            form={form}
            setForm={setForm}
            isCreate={!isEdit}
            onSlugInput={
              !isEdit
                ? (value) => {
                    slugTouchedRef.current = true;
                    setForm((p) => ({ ...p, codigo_interno: value }));
                  }
                : undefined
            }
            onSlugBlur={
              !isEdit
                ? () =>
                    setForm((p) => ({
                      ...p,
                      codigo_interno: slugify(p.codigo_interno),
                    }))
                : undefined
            }
          />
        </Grid>

        <Grid item xs={12}>
          {isEdit ? (
            <PermissionsCard companyId={form.id} />
          ) : (
            <PermissionsCard draftUsers={draftAclUsers} onDraftUsersChange={setDraftAclUsers} />
          )}
        </Grid>

        {isEdit ? (
          <>
            <Grid item xs={12}>
              <IdentityVisualCard form={form} setForm={setForm} onBusyUpload={setBusyUpload} />
            </Grid>

            <Grid item xs={12} md={7}>
              <ContactCard form={form} setForm={setForm} />
            </Grid>

            <Grid item xs={12} md={5}>
              <AddressLegalCard form={form} setForm={setForm} />
            </Grid>

            <Grid item xs={12}>
              <SocialsCard form={form} setForm={setForm} />
            </Grid>

            <Grid item xs={12}>
              <Accordion expanded={advancedOpen} onChange={(_, exp) => setAdvancedOpen(exp)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="company-advanced-content">
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", textAlign: "left" }}>
                    <Typography fontWeight={600}>{t("addCompanyForm.advanced.title")}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("addCompanyForm.advanced.summary")}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <RegionsCard regioes={regioes} setRegioes={setRegioes} />
                    </Grid>
                    <Grid item xs={12}>
                      <CepRangesCard regionKeys={regionKeys} cepRanges={cepRanges} setCepRanges={setCepRanges} />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </>
        ) : null}
      </Grid>

      <ActionBar onClose={onClose} isFetching={isLoading} disabled={disabled}>
        {busyUpload ? t("addCompanyForm.uploadingLogo") : null}
      </ActionBar>
    </Paper>
  );
}
